import { Head } from '@inertiajs/react'
import { FileText, FolderOpen } from 'lucide-react'
import { MainLayout } from '@/components/layouts'
import { Breadcrumb } from '@/components/ui/Breadcrumb'
import { ArticleCard } from '@/components/content'
import type { Article, BreadcrumbItem } from '@/types'

interface CategoryData {
  id: number
  name: string
  slug: string
  description: string | null
}

interface ContentCategoryProps {
  category: CategoryData
  articles: Article[]
  breadcrumb: BreadcrumbItem[]
}

export default function ContentCategory({ category, articles, breadcrumb }: ContentCategoryProps) {
  return (
    <MainLayout title={category.name}>
      <Head title={`${category.name} - Bibliothèque`} />

      <div>
        {/* Breadcrumb */}
        <div className="mb-6">
          <Breadcrumb items={breadcrumb} />
        </div>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-tessan-cream rounded-lg flex items-center justify-center">
              <FolderOpen className="w-5 h-5 text-tessan-primary" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-gray-900">
                {category.name}
              </h1>
              <p className="text-sm text-gray-500">
                {articles.length} {articles.length === 1 ? 'article' : 'articles'}
              </p>
            </div>
          </div>

          {category.description && (
            <p className="mt-4 text-gray-600">
              {category.description}
            </p>
          )}
        </div>

        {/* Articles List */}
        {articles.length > 0 ? (
          <div className="space-y-4">
            {articles.map((article) => (
              <ArticleCard
                key={article.id}
                article={article}
                categorySlug={category.slug}
              />
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Aucun article
            </h3>
            <p className="text-gray-500">
              Cette catégorie ne contient pas encore d'articles.
            </p>
          </div>
        )}
      </div>
    </MainLayout>
  )
}
